import {
  Colors,
  IcFolder,
  IcInStore,
  IcPlayV2,
  InputSearch,
  Text,
} from 'basic-ui-lib';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import { useGetAllStores } from 'modules/dashboard/services/hooks';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';
import { IStore } from 'modules/store/interfaces/api';
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import IcCheckBox from 'basic-ui-lib/components/Checkbox/assets/IcCheckBox.svg';
import IcCheckBoxSelected from 'basic-ui-lib/components/Checkbox/assets/IcCheckboxSelected.svg';
import { formatDuration } from 'utils/time';
import mediaApis from 'modules/media/services/apis';
import toast from 'utils/toast';

export interface MoveToStoresModalRef {
  open: (media: IMediaItem) => void;
  close: () => void;
}

interface MoveToStoresModalProps {}

const MoveToStoresModal = forwardRef<
  MoveToStoresModalRef,
  MoveToStoresModalProps
>((_, ref) => {
  const [visible, setVisible] = useState(false);
  const [media, setMedia] = useState<IMediaItem | null>(null);
  const [search, setSearch] = useState('');
  const { stores } = useGetAllStores(true);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);

  console.log('stores', stores);
  console.log('media', media);
  useImperativeHandle(ref, () => ({
    open: (selectedMedia: IMediaItem) => {
      setMedia(selectedMedia);
      setSearch('');
      setSelectedStoreIds([]);
      setVisible(true);
    },

    close: () => {
      setVisible(false);
    },
  }));

  const filteredStores = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return stores;
    }

    return stores.filter((store) =>
      store.storeName.toLowerCase().includes(keyword),
    );
  }, [stores, search]);

  const handleToggleStore = (storeId: string) => {
    setSelectedStoreIds((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId],
    );
  };

  const handleConfirm = async () => {
    if (!media || selectedStoreIds.length === 0) {
      return;
    }

    try {
      const res = await mediaApis.moveToStore(media.id, selectedStoreIds);
      if (res?.data?.data) {
        toast.success('Move to stores successful');
      }
    } catch (error) {
      toast.error('Failed to move to stores');
    }

    setVisible(false);
  };

  if (!visible || !media) {
    return null;
  }

  return (
    <BaseModal
      title='Move to Stores'
      isOpen={visible}
      width={700}
      onClose={() => setVisible(false)}
      okText='OK'
      onOk={handleConfirm}
      disabled={selectedStoreIds.length === 0}
    >
      <Container>
        {/* Selected media */}
        <MediaCard>
          <ThumbnailWrapper>
            {media.type === MediaType.FOLDER ? (
              <IcFolder />
            ) : (
              <>
                <Thumbnail src={media.type === MediaType.VIDEO ? media.thumbnail : media.url} alt={media.name} />

                {media.type === MediaType.VIDEO && (
                  <>
                    <PlayButton>
                      <IcPlayV2 />
                    </PlayButton>

                    <Duration>{formatDuration(media.duration ?? 0)}</Duration>
                  </>
                )}
              </>
            )}
          </ThumbnailWrapper>

          <MediaInfo>
            <Text title_h9 semibold>
              {media.name}
            </Text>

            <Text
              caption_2
              medium
              color='neutral.text.3'
              style={{ textTransform: 'capitalize', letterSpacing: '0.5px' }}
            >
              {media.type.toLocaleLowerCase()} · {media.size}
            </Text>

            <Location>
              <LocationBadge>Current location</LocationBadge>

              {/* <LocationName>{media.}</LocationName> */}
            </Location>
          </MediaInfo>
        </MediaCard>

        {/* Section title */}
        <Text content_2 medium color='neutral.text.3' left>
          SELECT DESTINATION STORES
        </Text>

        {/* Search */}
        <InputSearch
          value={search}
          onChange={(value) => setSearch(value)}
          placeholder='Search stores'
        />

        {/* Stores */}
        <StoreList>
          {filteredStores.map((store: IStore) => {
            const checked = selectedStoreIds.includes(store.storeId);

            return (
              <StoreItem
                key={store.storeId}
                checked={checked}
                onClick={() => handleToggleStore(store.storeId)}
              >
                <StoreLeft>
                  <StoreIconWrapper>
                    <IcInStore />
                  </StoreIconWrapper>

                  <StoreInfo>
                    <Text content_2 medium left>
                      {store.storeName}
                    </Text>

                    <Text caption_3 color='neutral.text.3' left>
                      {store.totalScreen} screens
                    </Text>
                  </StoreInfo>
                </StoreLeft>

                {checked ? (
                  <img src={IcCheckBoxSelected} alt='Checked' />
                ) : (
                  <img src={IcCheckBox} alt='Unchecked' />
                )}
              </StoreItem>
            );
          })}

          {!filteredStores.length && <EmptyState>No stores found</EmptyState>}
        </StoreList>
      </Container>
    </BaseModal>
  );
});

MoveToStoresModal.displayName = 'MoveToStoresModal';

export default MoveToStoresModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 0 24px;
`;

const MediaCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px;
  background: ${Colors.neutral.bg[2]};
  border: 1px solid ${Colors.neutral.bg[3]};
  border-radius: 12px;
`;

const ThumbnailWrapper = styled.div`
  position: relative;

  flex: 0 0 175px;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border-radius: 9px;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Duration = styled.span`
  position: absolute;
  right: 5px;
  bottom: 5px;
  padding: 2px 4px;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  font-size: 10px;
`;

const MediaInfo = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;

  margin-top: 8px;

  font-size: 13px;
`;

const LocationBadge = styled.span`
  padding: 2px 6px;

  color: #52647d;
  background: #e9edf4;

  border-radius: 4px;

  line-height: 18px;
`;

const StoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  padding-right: 12px;
  max-height: 280px;

  overflow-y: auto;

  scrollbar-width: thin;
`;

const StoreItem = styled.div<{ checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border: 1px solid
    ${({ checked }) => (checked ? '#8ddcb8' : `${Colors.neutral.bg[3]}`)};
  border-radius: 8px;
  background: ${({ checked }) =>
    checked ? '#f0fcf6' : `${Colors.neutral.bg[2]}`};
  cursor: pointer;
  transition: 0.15s ease;
  &:hover {
    border-color: #aeb7c5;
  }
`;

const StoreLeft = styled.div`
  display: flex;
  align-items: center;

  min-width: 0;
`;

const StoreIconWrapper = styled.div`
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  border: 1px solid ${Colors.neutral.bg[3]};
  border-radius: 8px;

  background: #fff;
`;

const StoreInfo = styled.div`
  min-width: 0;
  margin-left: 16px;
`;

const EmptyState = styled.div`
  padding: 30px 0;

  text-align: center;

  color: #8b95a6;

  font-size: 14px;
`;
